import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "./SharedIconsStyle.css";
import "./ChatHistoryV2.css";

import {
  Button,
  Input,
  Spinner,
  ThemeToggle,
  cn,
  toast
} from "../../../styles/facemeet-ui";

import { logger } from "../../../log/logger";

import {
  IconBack,
  IconInbox,
  IconAlert
} from "./SharedIcons";

export function ChatHistoryV2() {
  const { roomId } = useParams();
  const navigate = useNavigate();

  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chatType, setChatType] = useState("group");
  const [otherUserId, setOtherUserId] = useState("");
  const [inputValue, setInputValue] = useState("");

  const API_URL = process.env.REACT_APP_SERVER_URL;
  const myUserId =
    localStorage.getItem("userEmail") ||
    localStorage.getItem("userId") || "";

  /* ── Load group chat ──────────────────────────────────────────── */
  const loadGroupChat = async () => {
    logger.debug(`Loading group chat history | room=${roomId}`);
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_URL}/v2/group-chat/${roomId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setChatHistory(data);
    } catch (err) {
      logger.error(`Group chat history failed | room=${roomId}`, err);
      setError("Failed to load chat history. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Load personal chat ───────────────────────────────────────── */
  const loadPersonalChat = async (otherId) => {
    if (!otherId) return;
    logger.debug(`Loading personal chat | me=${myUserId} other=${otherId}`);
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_URL}/v2/personal-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId,
          senderId: myUserId,
          otherUserId: otherId,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setChatHistory(data);
    } catch (err) {
      logger.error(`Personal chat history failed | room=${roomId}`, err);
      setError("Failed to load personal chat. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Reload on tab / userId change ────────────────────────────── */
  useEffect(() => {
    if (chatType === "group") {
      loadGroupChat();
    } else if (chatType === "personal" && otherUserId) {
      loadPersonalChat(otherUserId);
    } else {
      setChatHistory([]);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, chatType, otherUserId]);

  const formatTime = (ts) =>
    new Date(ts).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });

  const loadPersonal = () => {
    const v = inputValue.trim().toLowerCase();
    if (!v) {
      toast.warning("Enter the other person's email first");
      return;
    }
    setOtherUserId(v);
  };

  return (
    <div className="fm-history-page">
      <div className="fm-ambient" aria-hidden="true" />

      <div className="fm-history-theme">
        <ThemeToggle />
      </div>

      <div className="fm-history-wrap">
        {/* HEADER */}
        <div className="fm-history-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 className="fm-history-head__title">Chat history</h1>
            <div className="fm-history-head__meta">
              <span>Room</span>
              <span className="fm-history-head__room">{roomId}</span>
              {!loading && !error && (
                <span>· {chatHistory.length} {chatHistory.length === 1 ? "message" : "messages"}</span>
              )}
            </div>
          </div>
          <Button variant="secondary" onClick={() => navigate("/v2")} leadingIcon={<IconBack size={16} />}>
            Back
          </Button>
        </div>

        {/* TAB SWITCHER */}
        <div className="fm-history-segmented" role="tablist" aria-label="Chat type">
          <button
            type="button"
            role="tab"
            aria-selected={chatType === "group"}
            className={cn("fm-history-segmented__btn", chatType === "group" && "fm-history-segmented__btn--active")}
            onClick={() => { setChatType("group"); setOtherUserId(""); setInputValue(""); }}
          >
            Group
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={chatType === "personal"}
            className={cn("fm-history-segmented__btn", chatType === "personal" && "fm-history-segmented__btn--active")}
            onClick={() => { setChatType("personal"); setChatHistory([]); }}
          >
            Private
          </button>
        </div>

        {/* PERSONAL SEARCH */}
        {chatType === "personal" && (
          <div className="fm-history-search">
            <Input
              label="Other participant"
              placeholder="Enter email or participant ID…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadPersonal(); }}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              hint="Enter the email of the person you want to see private chats with"
            />
            <Button variant="primary" onClick={loadPersonal}>Load</Button>
          </div>
        )}

        {/* STATES */}
        {chatType === "personal" && !otherUserId && (
          <div className="fm-history-block">
            <div className="fm-history-block__icon"><IconInbox size={22} /></div>
            <div className="fm-history-block__title">Find a conversation</div>
            <p className="fm-history-block__body">
              Enter the other person's email or participant ID above, then press Load.
            </p>
          </div>
        )}

        {error && (
          <div className="fm-history-block fm-history-block--error" role="alert">
            <div className="fm-history-block__icon"><IconAlert size={22} /></div>
            <div className="fm-history-block__title">Couldn't load chat</div>
            <p className="fm-history-block__body">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => chatType === "group" ? loadGroupChat() : loadPersonalChat(otherUserId)}
            >
              Try again
            </Button>
          </div>
        )}

        {loading && (
          <div className="fm-history-block">
            <Spinner size="lg" />
            <p className="fm-history-block__body">Loading messages…</p>
          </div>
        )}

        {!loading && !error && chatHistory.length === 0 && (otherUserId || chatType === "group") && (
          <div className="fm-history-block">
            <div className="fm-history-block__icon"><IconInbox size={22} /></div>
            <div className="fm-history-block__title">No messages found</div>
            <p className="fm-history-block__body">
              {chatType === "group"
                ? "This room hasn't had any group messages yet."
                : "No private messages between you and that user in this room."}
            </p>
          </div>
        )}

        {!loading && chatHistory.length > 0 && (
          <div className="fm-history-list">
            {chatHistory.map((msg, i) => (
              <div
                key={msg._id || `${msg.timestamp}-${i}`}
                className={cn("fm-history-msg", msg.senderId === myUserId && "fm-history-msg--own")}
              >
                <div className="fm-history-msg__head">
                  <span className="fm-history-msg__sender">
                    {msg.senderId === myUserId ? "You" : (msg.senderName || msg.senderId)}
                  </span>
                  <span className="fm-history-msg__time">{formatTime(msg.timestamp)}</span>
                </div>
                <div className="fm-history-msg__content">{msg.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Default export for convenience
   ───────────────────────────────────────────────────────────────────── */
export default ChatHistoryV2;